/**
 * A custom element that switches between product tab panels.
 *
 * The tab bar is built from the panels themselves, so the labels always match
 * the blocks the merchant configured. Panels render visible and stacked, and
 * only collapse once this upgrades, so the products stay readable without
 * JavaScript. Follows the tabs pattern: roving tabindex, arrow keys, Home and
 * End.
 */
class ZuraBestSellers extends HTMLElement {
  #index = 0;

  connectedCallback() {
    this.tabBar = this.querySelector('.zura-best-sellers__tabs');
    this.panelWrap = this.querySelector('.zura-best-sellers__panels');
    this.panels = Array.from(this.querySelectorAll('.zura-best-sellers__panel'));

    if (!this.panels.length) return;

    if (this.panels.length > 1 && this.tabBar) {
      this.#buildTabs();
      this.tabs = Array.from(this.tabBar.querySelectorAll('.zura-best-sellers__tab'));
      this.tabBar.hidden = false;

      this.tabBar.addEventListener('click', (event) => {
        const tab = event.target.closest('.zura-best-sellers__tab');
        if (tab) this.#activate(this.tabs.indexOf(tab), { focus: false });
      });

      this.tabBar.addEventListener('keydown', (event) => this.#handleKeydown(event));
    }

    // Mark the active panel before switching the CSS to enhanced mode, so no
    // frame renders with every panel hidden.
    this.#activate(0, { focus: false });
    if (this.panelWrap) this.panelWrap.setAttribute('data-enhanced', '');
  }

  #buildTabs() {
    const prefix = this.closest('[id]')?.id ?? 'zura-best-sellers';
    this.tabBar.innerHTML = '';

    this.panels.forEach((panel, index) => {
      const tabId = `${prefix}-tab-${index}`;
      const panelId = `${prefix}-panel-${index}`;

      panel.id = panelId;
      panel.setAttribute('aria-labelledby', tabId);

      const button = document.createElement('button');
      button.type = 'button';
      button.id = tabId;
      button.className = 'zura-best-sellers__tab';
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', panelId);
      button.textContent = panel.dataset.label || 'Products';

      this.tabBar.appendChild(button);
    });
  }

  /** @param {KeyboardEvent} event - The keydown event. */
  #handleKeydown(event) {
    if (!event.target.closest('.zura-best-sellers__tab')) return;

    const last = this.tabs.length - 1;
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = this.#index === last ? 0 : this.#index + 1;
        break;
      case 'ArrowLeft':
        next = this.#index === 0 ? last : this.#index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(next, { focus: true });
  }

  /**
   * @param {number} index - The tab to activate.
   * @param {{focus: boolean}} options - Whether to move focus to the tab.
   */
  #activate(index, { focus }) {
    this.#index = index;

    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    if (!this.tabs) return;

    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    if (focus) this.tabs[index]?.focus();
  }
}

if (!customElements.get('zura-best-sellers-component')) {
  customElements.define('zura-best-sellers-component', ZuraBestSellers);
}
